import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toastError } from '../../../utils/toast';
import API_BASE_URL from '../../../config/api';

const GroceryShopFormModal = ({ isOpen, onClose, shop, onSubmit, saving = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    image_url: '',
    banner_url: '',
  });
  const [errors, setErrors] = useState({});
  const [uploadingField, setUploadingField] = useState(null);

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.name || '',
        description: shop.description || '',
        is_active: shop.is_active !== false,
        image_url: shop.image_url || '',
        banner_url: shop.banner_url || '',
      });
    } else {
      setFormData({ name: '', description: '', is_active: true, image_url: '', banner_url: '' });
    }
    setErrors({});
  }, [shop, isOpen]);

  const handleImageUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, [field]: 'Le fichier doit être une image' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, [field]: "L'image ne doit pas dépasser 5MB" }));
      return;
    }

    try {
      setUploadingField(field);
      setErrors((prev) => ({ ...prev, [field]: null }));

      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'grocery-shops');

      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/management/upload/image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: uploadData,
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const result = await response.json();
      setFormData((prev) => ({ ...prev, [field]: result.image_url }));
    } catch (error) {
      console.error('Erreur upload:', error);
      setErrors((prev) => ({ ...prev, [field]: "Erreur lors de l'upload de l'image" }));
    } finally {
      setUploadingField(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError('Le nom du magasin est obligatoire.');
      return;
    }

    await onSubmit({
      name: formData.name.trim(),
      description: formData.description || null,
      is_active: !!formData.is_active,
      image_url: formData.image_url || null,
      banner_url: formData.banner_url || null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">
            {shop ? 'Modifier mon magasin' : 'Créer mon magasin'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom du magasin *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Épicerie Mama Afrika"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                placeholder="Présentez votre magasin..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image (logo)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'image_url')}
                disabled={uploadingField === 'image_url'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              <p className="mt-1 text-xs text-gray-500">
                Recommandé : image PNG avec fond transparent.
              </p>
              {uploadingField === 'image_url' && (
                <div className="mt-2 flex items-center gap-2 text-green-700 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#054625]" />
                  Upload en cours...
                </div>
              )}
              {errors.image_url && <p className="mt-1 text-sm text-red-600">{errors.image_url}</p>}
              {formData.image_url && (
                <div className="mt-3 border border-gray-200 rounded-lg p-2">
                  <img
                    src={formData.image_url}
                    alt="Aperçu logo"
                    className="w-20 h-20 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bannière</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'banner_url')}
                disabled={uploadingField === 'banner_url'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              <p className="mt-1 text-xs text-gray-500">
                Image large affichée en haut de la fiche publique de votre magasin.
              </p>
              {uploadingField === 'banner_url' && (
                <div className="mt-2 flex items-center gap-2 text-green-700 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#054625]" />
                  Upload en cours...
                </div>
              )}
              {errors.banner_url && <p className="mt-1 text-sm text-red-600">{errors.banner_url}</p>}
              {formData.banner_url && (
                <div className="mt-3 border border-gray-200 rounded-lg p-2">
                  <img
                    src={formData.banner_url}
                    alt="Aperçu bannière"
                    className="w-full h-24 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x200?text=Image+non+disponible';
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="grocery_shop_is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 text-green-700 focus:ring-green-700 border-gray-300 rounded"
              />
              <label htmlFor="grocery_shop_is_active" className="ml-2 text-sm text-gray-700">
                Magasin actif (visible des clients)
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Enregistrement...' : (shop ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroceryShopFormModal;
